import { getMutedColor, isEdgeBlocking } from '../../FlowchartView/components/EdgeRenderer/utils';

const SOLID = 'rgba(255, 255, 255, 0.7)';
const BLOCKING = 'rgba(255, 255, 255, 0.35)';
const NOTE = 'rgba(142, 180, 212, 0.62)';

/** A small chevron at the end of a path, in the same coordinates as the line. */
export function arrowTip(pathD) {
  if (!pathD) return '';
  const end = pathD.match(/L\s*(-?\d*\.?\d+)\s+(-?\d*\.?\d+)\s*$/);
  if (!end) return '';
  const tipX = Number(end[1]);
  const tipY = Number(end[2]);
  const before = pathD.slice(0, end.index);
  const pairs = before.match(/(-?\d*\.?\d+)\s+(-?\d*\.?\d+)\s*$/);
  if (!pairs) return '';
  const dx = tipX - Number(pairs[1]);
  const dy = tipY - Number(pairs[2]);
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const back = 7;
  const wing = 3.2;
  const bx = tipX - ux * back;
  const by = tipY - uy * back;
  return `M ${bx - uy * wing} ${by + ux * wing} L ${tipX} ${tipY} L ${bx + uy * wing} ${by - ux * wing}`;
}

/** Box the strokes sit in, so the picture has a real size and can be moved as one layer. */
export function wireFrame(layers) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const line = /[ML]\s*(-?\d*\.?\d+)\s+(-?\d*\.?\d+)/gi;
  const arc = /A\s+-?\d*\.?\d+\s+-?\d*\.?\d+\s+-?\d*\.?\d+\s+[01]\s+[01]\s+(-?\d*\.?\d+)\s+(-?\d*\.?\d+)/gi;
  (layers || []).forEach((layer) => {
    const text = `${layer.lines || ''} ${layer.arrows || ''}`;
    let match = line.exec(text);
    while (match) {
      const x = Number(match[1]);
      const y = Number(match[2]);
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
      match = line.exec(text);
    }
    line.lastIndex = 0;
    match = arc.exec(text);
    while (match) {
      const x = Number(match[1]);
      const y = Number(match[2]);
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
      match = arc.exec(text);
    }
    arc.lastIndex = 0;
  });
  if (!Number.isFinite(minX)) return null;
  // Room for the corner arc and the arrow tip, which stick out past the path.
  const pad = 16;
  return {
    minX: minX - pad,
    minY: minY - pad,
    width: maxX - minX + pad * 2,
    height: maxY - minY + pad * 2,
  };
}

/**
 * Fold every wire into a few strokes. The board does not hover or drag arrows,
 * so it does not need one element and a hit area per line.
 */
export function buildWireLayers(edges, tasks) {
  const byId = new Map();
  (tasks || []).forEach((task) => {
    if (task?.id) byId.set(task.id, task);
  });
  const layers = new Map();
  (edges || []).forEach((edge) => {
    if (!edge?.pathD) return;
    const note = Boolean(edge.isNoteStem);
    const blocking = !note && isEdgeBlocking(byId.get(edge.from), edge);
    const branch = !note && edge.isFromDecision && edge.branch;
    const color = branch ? (edge.branchColor || '#a78bfa') : null;
    const stroke = note ? NOTE : (color ? (blocking ? getMutedColor(color) : color) : (blocking ? BLOCKING : SOLID));
    const width = note ? 1.5 : 1;
    const dash = blocking ? '4 4' : 'none';
    const key = `${stroke}|${width}|${dash}`;
    const layer = layers.get(key) || { key, stroke, width, dash, lines: '', arrows: '' };
    layer.lines += `${edge.pathD} `;
    if (!note) layer.arrows += `${arrowTip(edge.pathD)} `;
    layers.set(key, layer);
  });
  return [...layers.values()];
}
